/**
 * TaxiRequestSheet — customer-facing modal to request a taxi (internal OR external).
 *
 * Internal flow:
 *   1) Customer fills name/phone
 *   2) Taps "الموقع الحالي" → sets pickup via GPS
 *   3) Taps "الوجهة" → sets dropoff via GPS (or map picker later)
 *   4) Once both coords are set, we auto-call /taxi/quote/meter which returns
 *      { distance_km, original_price, final_price, discount_pct, is_night }
 *   5) The card shows the original crossed-out + discounted price, with distance and rate.
 *
 * External flow: unchanged (zones/gov based).
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { api, formatIQD } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Car, Route, MapPin, Moon, Sun, Search, Target, Navigation } from "lucide-react";
import { toast } from "sonner";
import LocationPickerMap from "@/components/LocationPickerMap";

const TAXI_STORAGE_KEY = "kh_my_taxi_requests";
function saveTaxiRequestId(id) {
  try {
    const raw = localStorage.getItem(TAXI_STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!list.includes(id)) list.unshift(id);
    localStorage.setItem(TAXI_STORAGE_KEY, JSON.stringify(list.slice(0, 100)));
  } catch { /* ignore */ }
}

export default function TaxiRequestSheet({ type = "internal", open, onOpenChange }) {
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  // Internal (meter-based)
  const [pickup, setPickup] = useState(null);   // {lat, lng}
  const [dropoff, setDropoff] = useState(null);
  const [pickupArea, setPickupArea] = useState("");
  const [dropoffArea, setDropoffArea] = useState("");
  const [meterBusy, setMeterBusy] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(null); // "pickup" | "dropoff" | null
  // Dropoff area autocomplete state
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [dropoffSearching, setDropoffSearching] = useState(false);
  const [dropoffFocused, setDropoffFocused] = useState(false);
  const dropoffSearchTimer = useRef(null);
  // External
  const [govs, setGovs] = useState([]);
  const [toGov, setToGov] = useState("");
  const [passengers, setPassengers] = useState(1);
  const [tripType, setTripType] = useState("one_way");
  // Shared
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    if (type === "external") {
      api.get("/taxi/govs").then(r => setGovs(r.data || [])).catch(() => {});
    }
  }, [open, type]);

  // Auto-fetch meter quote once both pickup and dropoff coords exist
  useEffect(() => {
    if (type !== "internal") return;
    if (!pickup || !dropoff) { setQuote(null); return; }
    let cancelled = false;
    setMeterBusy(true);
    api.get("/taxi/quote/meter", {
      params: {
        from_lat: pickup.lat, from_lng: pickup.lng,
        to_lat: dropoff.lat, to_lng: dropoff.lng,
      },
    })
      .then(r => { if (!cancelled) setQuote(r.data); })
      .catch(() => { if (!cancelled) toast.error("تعذّر حساب السعر"); })
      .finally(() => { if (!cancelled) setMeterBusy(false); });
    return () => { cancelled = true; };
  }, [type, pickup, dropoff]);

  const getGPS = (setter, areaSetter, label) => {
    if (!navigator.geolocation) { toast.error("GPS غير متاح"); return; }
    navigator.geolocation.getCurrentPosition(
      pos => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setter(coords);
        toast.success(`✅ ${label}`);
        // Reverse-geocode to Arabic area name (best-effort)
        if (areaSetter) {
          api.get(`/geo/reverse?lat=${coords.lat}&lng=${coords.lng}`)
            .then(r => {
              const name = r.data?.display_name || r.data?.area || "";
              if (name) areaSetter(prev => prev || name);
            })
            .catch(() => { /* ignore */ });
        }
      },
      () => toast.error("تعذّر تحديد الموقع"),
      { enableHighAccuracy: true, timeout: 8000 },
    );
  };

  const fetchExternalQuote = async () => {
    setLoading(true);
    try {
      if (!toGov) { toast.error("اختر المحافظة"); return; }
      const { data } = await api.get(`/taxi/quote/external?to_gov=${toGov}&passengers=${passengers}&trip_type=${tripType}`);
      setQuote(data);
    } catch { toast.error("تعذّر الحصول على السعر"); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    if (!customerName || !customerPhone) { toast.error("املأ الاسم والهاتف"); return; }
    if (!quote) { toast.error(type === "internal" ? "حدّد موقعك ووجهتك" : "احصل على السعر أولاً"); return; }
    setLoading(true);
    try {
      if (type === "internal") {
        if (!pickup || !dropoff) { toast.error("حدّد موقعي الانطلاق والوصول"); return; }
        const { data } = await api.post("/taxi/internal/request", {
          customer_name: customerName, customer_phone: customerPhone,
          pickup_lat: pickup.lat, pickup_lng: pickup.lng,
          dropoff_lat: dropoff.lat, dropoff_lng: dropoff.lng,
          pickup_zone_id: "", dropoff_zone_id: "",
          pickup_area: pickupArea, dropoff_area: dropoffArea,
          quoted_price: quote.final_price,
          notes: [notes, dropoffArea ? `وجهة: ${dropoffArea}` : ""].filter(Boolean).join(" · "),
        });
        if (data?.id) saveTaxiRequestId(data.id);
      } else {
        const { data } = await api.post("/taxi/external/request", {
          customer_name: customerName, customer_phone: customerPhone,
          to_gov: toGov, passengers, trip_type: tripType,
          quoted_price: quote.final_price, notes,
        });
        if (data?.id) saveTaxiRequestId(data.id);
      }
      toast.success("✅ تم إرسال طلب التكسي — انتظر تأكيد السائق");
      onOpenChange(false);
      // Reset
      setQuote(null); setNotes(""); setPickup(null); setDropoff(null);
    } catch { toast.error("فشل الإرسال"); }
    finally { setLoading(false); }
  };

  const hasDiscount = quote && Number(quote.discount_pct || 0) > 0 && Number(quote.original_price || 0) > Number(quote.final_price || 0);

  return (
    <>
    <Sheet
      // When the map picker is showing, disable the Sheet's modal behaviour
      // (Radix uses react-remove-scroll which blocks `touchmove` on any
      // element outside the dialog — that killed Leaflet dragging on mobile).
      modal={!showMapPicker}
      open={open}
      onOpenChange={(v) => {
        if (!v && showMapPicker) return;
        onOpenChange(v);
      }}
    >
      <SheetContent
        side="bottom"
        className="h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => { if (showMapPicker) e.preventDefault(); }}
        onInteractOutside={(e) => { if (showMapPicker) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (showMapPicker) e.preventDefault(); }}
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            {type === "internal" ? <><Car className="size-5 text-primary" /> طلب تكسي داخلي</> : <><Route className="size-5 text-primary" /> طلب تكسي خارجي</>}
          </SheetTitle>
        </SheetHeader>

        <div className="space-y-3 pt-4">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>الاسم</Label><Input value={customerName} onChange={e => setCustomerName(e.target.value)} data-testid="taxi-cust-name" /></div>
            <div><Label>الهاتف</Label><Input type="tel" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} data-testid="taxi-cust-phone" /></div>
          </div>

          {type === "internal" ? (
            <>
              <div className="space-y-2">
                <div>
                  <Button size="lg" variant={pickup ? "default" : "outline"}
                          className="w-full justify-start gap-2"
                          onClick={() => getGPS(setPickup, setPickupArea, "تم تحديد موقعك")}
                          data-testid="taxi-pickup-gps">
                    <MapPin className="size-4" />
                    {pickup ? `✅ الموقع الحالي — ${pickup.lat.toFixed(4)}, ${pickup.lng.toFixed(4)}` : "① اضغط لتحديد موقعك الحالي"}
                  </Button>
                  <Input
                    className="mt-1.5"
                    value={pickupArea}
                    onChange={e => setPickupArea(e.target.value)}
                    placeholder="أو اكتب اسم المنطقة يدوياً (مثل: حي المعلمين)"
                    data-testid="taxi-pickup-area"
                  />
                </div>

                <div>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute end-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
                      <Input
                        className="pe-9"
                        value={dropoffArea}
                        onChange={e => {
                          const v = e.target.value;
                          setDropoffArea(v);
                          // Reset picked coords if user is rewriting the area
                          if (dropoff && dropoffSuggestions.every(s => s.area !== v && s.name !== v)) {
                            // keep coords until user picks a new suggestion
                          }
                          // Debounced search
                          if (dropoffSearchTimer.current) clearTimeout(dropoffSearchTimer.current);
                          if (!v || v.trim().length < 2) {
                            setDropoffSuggestions([]); setDropoffSearching(false);
                            return;
                          }
                          setDropoffSearching(true);
                          dropoffSearchTimer.current = setTimeout(async () => {
                            let results = [];
                            try {
                              // 1a) Match against admin-drawn taxi zones FIRST — these are
                              // authoritative for pricing, so if the typed destination
                              // sits inside a drawn zone the pinpoint-on-map must land
                              // there, not on an unrelated Nominatim result.
                              const z = await api.get(`/taxi/zones/search?q=${encodeURIComponent(v.trim())}&limit=6`);
                              if (Array.isArray(z.data) && z.data.length > 0) results = z.data;
                            } catch { /* ignore, try proxy next */ }
                            try {
                              // 1b) Backend-proxied Nominatim (cached hits are instant)
                              const r = await api.get(`/geo/search?q=${encodeURIComponent(v.trim())}&limit=6`);
                              if (Array.isArray(r.data) && r.data.length > 0) {
                                const existing = new Set(results.map(x => `${x.lat},${x.lng}`));
                                r.data.forEach((x) => { if (!existing.has(`${x.lat},${x.lng}`)) results.push(x); });
                              }
                            } catch { /* ignore, try direct next */ }
                            // 2) Fall back to browser-direct Nominatim (users have unique IPs)
                            // Restricted to a ~30km box around Khanaqin so results are locally relevant.
                            if (results.length === 0) {
                              try {
                                const resp = await fetch(
                                  `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(v.trim())}&format=json&addressdetails=1&limit=6&countrycodes=iq&viewbox=45.15,34.55,45.60,34.15&bounded=1&accept-language=ar`,
                                  { headers: { "Accept": "application/json" } }
                                );
                                if (resp.ok) {
                                  const j = await resp.json();
                                  results = (j || []).map(item => {
                                    const a = item.address || {};
                                    const area = a.suburb || a.neighbourhood || a.village || a.town || a.hamlet ||
                                                 a.city_district || a.district || a.city || a.county || a.state || item.display_name || "";
                                    let name = item.display_name || area || v;
                                    if (typeof name === "string" && name.includes(",")) {
                                      name = name.split(",").slice(0, 3).map(s => s.trim()).join(", ");
                                    }
                                    return { name, area, lat: parseFloat(item.lat), lng: parseFloat(item.lon), kind: item.type || item.class || "" };
                                  }).filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lng));
                                }
                              } catch { /* ignore */ }
                            }
                            setDropoffSuggestions(results);
                            setDropoffSearching(false);
                          }, 400);
                        }}
                        onFocus={() => setDropoffFocused(true)}
                        onBlur={() => setTimeout(() => setDropoffFocused(false), 150)}
                        placeholder="② اكتب اسم منطقة الوجهة (مثل: حي رمضان)"
                        data-testid="taxi-dropoff-area"
                      />
                    </div>
                    {/* Autocomplete dropdown */}
                    {dropoffFocused && (dropoffSearching || dropoffSuggestions.length > 0) && (
                      <div className="absolute top-full inset-x-0 mt-1 z-[60] rounded-xl bg-popover border shadow-2xl overflow-hidden" data-testid="taxi-dropoff-suggestions">
                        {dropoffSearching && (
                          <div className="px-3 py-2 text-xs text-muted-foreground flex items-center gap-1.5">
                            <Loader2 className="size-3 animate-spin" /> جاري البحث…
                          </div>
                        )}
                        {!dropoffSearching && dropoffSuggestions.length === 0 && (
                          <div className="px-3 py-2 text-xs text-muted-foreground">لا توجد نتائج</div>
                        )}
                        {dropoffSuggestions.map((s, i) => (
                          <button
                            key={`${s.lat}-${s.lng}-${i}`}
                            type="button"
                            className="w-full text-start px-3 py-2 hover:bg-accent transition flex items-start gap-2 border-t first:border-t-0"
                            onClick={() => {
                              setDropoff({ lat: s.lat, lng: s.lng });
                              setDropoffArea(s.area || s.name);
                              setDropoffSuggestions([]);
                              setDropoffFocused(false);
                              toast.success(`تم اختيار ${s.area || s.name}`);
                            }}
                            data-testid={`taxi-dropoff-suggestion-${i}`}
                          >
                            <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-semibold line-clamp-1">{s.area || s.name}</div>
                              {s.name && s.name !== s.area && (
                                <div className="text-[10px] text-muted-foreground line-clamp-1">{s.name}</div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Pinpoint on map button — opens interactive map centered on the picked area.
                      If the user typed a dropoff area but didn't tap a suggestion, we geocode
                      it ON DEMAND before opening the map so the arrow lands at the
                      destination (not the user's current location). */}
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full justify-center gap-2 mt-2 border-primary/60 bg-primary/5"
                    onClick={async () => {
                      if (!dropoff && dropoffArea && dropoffArea.trim()) {
                        // 1) Resolve against admin zones first — if the typed name
                        //    matches a drawn zone the map must open there.
                        try {
                          const z = await api.get(`/taxi/zones/search?q=${encodeURIComponent(dropoffArea.trim())}&limit=1`);
                          if (Array.isArray(z.data) && z.data[0] && Number.isFinite(z.data[0].lat)) {
                            setDropoff({ lat: z.data[0].lat, lng: z.data[0].lng });
                            setShowMapPicker("dropoff");
                            return;
                          }
                        } catch { /* fall through */ }
                        // 2) Fall back to global Nominatim
                        try {
                          const resp = await fetch(
                            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(dropoffArea.trim())}&format=json&limit=1&countrycodes=iq&viewbox=45.15,34.55,45.60,34.15&bounded=1&accept-language=ar`
                          );
                          if (resp.ok) {
                            const j = await resp.json();
                            if (j && j[0] && j[0].lat && j[0].lon) {
                              setDropoff({ lat: parseFloat(j[0].lat), lng: parseFloat(j[0].lon) });
                            }
                          }
                        } catch { /* non-fatal, map will open on pickup fallback */ }
                      }
                      setShowMapPicker("dropoff");
                    }}
                    data-testid="taxi-dropoff-map"
                  >
                    <Target className="size-4 text-primary" />
                    {dropoff ? "✅ عدّل المؤشر على الخريطة" : "🎯 حدّد الموقع بالضبط على الخريطة"}
                  </Button>

                  {/* Also: fallback GPS button (quick set of dropoff = my current location) */}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full justify-center gap-2 mt-1.5 text-xs text-muted-foreground"
                    onClick={() => getGPS(setDropoff, setDropoffArea, "تم تحديد وجهتك")}
                    data-testid="taxi-dropoff-gps"
                  >
                    <Navigation className="size-3.5" /> استخدم موقع GPS الحالي كوجهة
                  </Button>

                  {dropoff && (
                    <div className="text-[10px] text-emerald-600 mt-1 text-center" data-testid="taxi-dropoff-coords">
                      📍 {dropoff.lat.toFixed(4)}, {dropoff.lng.toFixed(4)}
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-muted-foreground text-center">
                  التطبيق يقيس المسافة تلقائياً بعد تحديد الموقع والوجهة
                </p>
              </div>
            </>
          ) : (
            <>
              <div><Label>المحافظة الوجهة</Label>
                <select className="w-full border rounded p-2 bg-background" value={toGov} onChange={e => setToGov(e.target.value)} data-testid="taxi-to-gov">
                  <option value="">— اختر —</option>
                  {govs.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>عدد الركاب</Label>
                  <Input type="number" min={1} max={10} value={passengers} onChange={e => setPassengers(Number(e.target.value) || 1)} data-testid="taxi-passengers" />
                </div>
                <div><Label>نوع الرحلة</Label>
                  <select className="w-full border rounded p-2 bg-background" value={tripType} onChange={e => setTripType(e.target.value)} data-testid="taxi-trip-type">
                    <option value="one_way">ذهاب فقط</option>
                    <option value="round_trip">ذهاب وإياب</option>
                    <option value="return_only">اياب فقط</option>
                  </select>
                </div>
              </div>
              <Button variant="secondary" className="w-full" onClick={fetchExternalQuote} disabled={loading} data-testid="taxi-quote-btn">
                {loading ? <Loader2 className="size-4 animate-spin" /> : "احسب السعر"}
              </Button>
            </>
          )}

          <div><Label>ملاحظات (اختياري)</Label><Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="عدد الحقائب، ملاحظة للسائق..." /></div>

          {/* Price display */}
          {type === "internal" && meterBusy && (
            <div className="rounded-xl bg-muted p-4 text-center flex items-center justify-center gap-2 text-sm">
              <Loader2 className="size-4 animate-spin" /> يحسب السعر...
            </div>
          )}

          {quote && (
            <div className="rounded-xl bg-primary/10 border border-primary/30 p-4 text-center space-y-2" data-testid="taxi-quote-card">
              <div className="text-xs text-muted-foreground">السعر النهائي</div>

              {hasDiscount ? (
                <div>
                  <div className="text-sm line-through text-muted-foreground" data-testid="taxi-original-price">
                    {formatIQD(quote.original_price)}
                  </div>
                  <div className="text-3xl font-black text-primary" data-testid="taxi-final-price">
                    {formatIQD(quote.final_price)}
                  </div>
                  <div className="inline-block mt-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                    🎁 خصم {quote.discount_pct}%
                  </div>
                </div>
              ) : (
                <div className="text-3xl font-black" data-testid="taxi-final-price">{formatIQD(quote.final_price)}</div>
              )}

              {quote.mode === "fixed_pair" && quote.fixed_pair && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-xs font-bold text-primary" data-testid="taxi-zone-pair">
                  🗺️ من <b>{quote.fixed_pair.from}</b> ← إلى <b>{quote.fixed_pair.to}</b>
                </div>
              )}

              {type === "internal" && (
                <div className="flex items-center justify-center gap-3 text-[11px] text-muted-foreground pt-1">
                  <span>📍 {quote.distance_km} كم</span>
                  <span className="opacity-40">•</span>
                  <span className="flex items-center gap-1">
                    {quote.is_night ? <Moon className="size-3" /> : <Sun className="size-3" />}
                    {formatIQD(quote.per_km_rate)}/كم {quote.is_night ? "(ليلي)" : "(نهاري)"}
                  </span>
                </div>
              )}

              {type === "external" && (
                <div className="text-xs">{quote.passengers} راكب × {formatIQD(quote.per_passenger)} {quote.trip_type === "round_trip" && "× 2 (ذهاب وإياب)"}</div>
              )}
            </div>
          )}

          <Button size="lg" className="w-full font-black" onClick={submit} disabled={!quote || loading} data-testid="taxi-submit-btn">
            {loading ? <Loader2 className="size-4 animate-spin" /> : "طلب تكسي الآن"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>

      {/* Map picker overlay — rendered OUTSIDE the Sheet component tree
          entirely, so Radix's react-remove-scroll cannot block Leaflet's
          `touchmove` events on mobile. This is the fix for the "pin does not
          drag on mobile" bug. */}
      {showMapPicker && createPortal(
        <LocationPickerMap
          initial={
            showMapPicker === "pickup"
              ? (pickup || { lat: 34.352, lng: 45.386 })
              : // For DROPOFF: prefer the already-selected dropoff coords.
                // If none yet, prefer any geocoded suggestion matching the typed
                // area name. ONLY as a very last resort fall back to pickup —
                // because the user said the arrow kept landing on their current
                // location instead of inside the destination.
                (dropoff
                  || (dropoffSuggestions.find(s => (s.area || s.name) === dropoffArea) &&
                      { lat: dropoffSuggestions.find(s => (s.area || s.name) === dropoffArea).lat,
                        lng: dropoffSuggestions.find(s => (s.area || s.name) === dropoffArea).lng })
                  || (dropoffSuggestions[0] &&
                      { lat: dropoffSuggestions[0].lat, lng: dropoffSuggestions[0].lng })
                  || pickup
                  || { lat: 34.352, lng: 45.386 })
          }
          onConfirm={({ lat, lng, address }) => {
            if (showMapPicker === "pickup") {
              setPickup({ lat, lng });
              if (address) setPickupArea(prev => prev || address);
            } else {
              setDropoff({ lat, lng });
              if (address) setDropoffArea(prev => prev || address);
            }
            setShowMapPicker(null);
            toast.success(showMapPicker === "pickup" ? "تم تحديد موقعك" : "تم تحديد وجهتك");
          }}
          onCancel={() => setShowMapPicker(null)}
        />,
        document.body,
      )}
    </>
  );
}
